import { Box, Skeleton } from '@mui/material';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AsyncSelectOrTextField } from 'src/components/forms/AsyncSelectOrTextField';
import { ITableColumn, TableList } from 'src/components/table';
import { EntityWithIdAndDescriptionFields } from 'src/types';
import { AFOPUHeader, AFOPURow } from '../hooks/useTablaValorAFOPU';
import { OperacionAFOPUFields, OperacionAFOPUTablaValorFields } from '../types/fields';

interface AFOPUTablaValoresEditorGridProps {
  formMethods: UseFormReturn<any>;
  headers: AFOPUHeader[];
  rows: AFOPURow[];
  aforos: any[];
  loadAforoTipos: any;
  isLoading: boolean;
  permiteEditar?: boolean;
}

const AFOPUTablaValoresEditorGrid = ({
  formMethods,
  headers,
  rows,
  aforos,
  loadAforoTipos,
  isLoading,
  permiteEditar = true
}: AFOPUTablaValoresEditorGridProps): JSX.Element => {

  const { control } = formMethods;

  const indexMap = useMemo(() => {
    const map = new Map<string, number>();
    aforos.forEach((v, idx) => {
      const codInst = v[OperacionAFOPUTablaValorFields.CodInstrumento];
      const codTipo = v[OperacionAFOPUTablaValorFields.CodOperacionTipo];
      map.set(`${codInst}-${codTipo}`, idx);
    });

    return map;
  }, [aforos]);

  const columns: ITableColumn[] = useMemo(() => {
    const cols: ITableColumn[] = [
      {
        label: 'Instrumento',
        onRenderCell: (row: AFOPURow) => <b>{row.descInstrumento}</b>
      }
    ];

    headers.forEach((header) => {
      cols.push({
        label: header.descOperacionTipo,
        onRenderCell: (row: AFOPURow) => {
          const celda = row.celdas[header.codOperacionTipo];
          if (!celda) return <span>-</span>;

          const key = `${celda[OperacionAFOPUTablaValorFields.CodInstrumento]}-${celda[OperacionAFOPUTablaValorFields.CodOperacionTipo]}`;
          const index = indexMap.get(key);

          if (index === undefined) return <Skeleton variant='text' />;

          const fieldPath = `${OperacionAFOPUFields.TablaValores}.${index}.${OperacionAFOPUTablaValorFields.Valor}`;

          return (
            <AFOPUTablaValorCelda
              control={control}
              name={fieldPath}
              permiteEditar={permiteEditar && celda.permiteEditar}
              loadOptions={loadAforoTipos}
            />
          );
        }
      });
    });

    return cols;
  }, [headers, indexMap, control, loadAforoTipos, permiteEditar]);

  return (
    <TableList
      entityList={rows}
      columns={columns}
      isLoading={isLoading}
      error={false}
    />
  );
};

export default AFOPUTablaValoresEditorGrid;

const AFOPUTablaValorCelda = ({
  control,
  name,
  permiteEditar,
  loadOptions
}: {
  control: any;
  name: string;
  permiteEditar: boolean;
  loadOptions: any;
}) => {
  return (
    <Box sx={{ py: 0.5, px: 0.5, minWidth: '140px' }}>
      <AsyncSelectOrTextField
        label=''
        loadOptions={loadOptions}
        control={control}
        name={name}
        disabled={!permiteEditar}
        valueField={EntityWithIdAndDescriptionFields.Description}
        descriptionField={EntityWithIdAndDescriptionFields.Description}
        nullOptionLabel="Otro"
        buttonType="icon"
        textFieldProps={{
          size: 'small',
          currency: true,
          percentage: true,
          saveFormattedValue: true
        }}
      />
    </Box>
  );
};
